import { type Writable, writable } from "svelte/store"

import type { PlanAnchor, PlanCard } from "@/types"

export type AnchorKey = "eur" | "usd" | "gbp"

// The display anchor the pricing cards should use, resolved from the
// visitor's country. See docs/architecture/billing.md.
//
// Defaults to the EUR anchor for the first paint (and SSR value) before the
// country is known; /geo switches to the USD or GBP anchor once it confirms a
// non-EU visitor. A failed/blocked probe safely stays on the EUR anchor.
export const anchorKey: Writable<AnchorKey> = writable("eur")

// The visitor's ISO country from the same probe (upcased alpha-2), or null
// until it lands / when the edge doesn't know.
export const geoCountry: Writable<string | null> = writable(null)

let started = false

// Fetches the uncached /geo probe once per page load and updates the
// anchor. CF-IPCountry isn't readable from JS, so this round-trip is how
// the country reaches the (edge-cached, country-neutral) HTML.
export async function loadGeo(): Promise<void> {
  if (started) return
  started = true
  try {
    const res = await fetch("/geo", { headers: { Accept: "application/json" } })
    if (!res.ok) return
    const data = (await res.json()) as {
      anchor?: string
      country?: string | null
    }
    if (
      data.anchor === "usd" ||
      data.anchor === "eur" ||
      data.anchor === "gbp"
    ) {
      anchorKey.set(data.anchor)
    }
    if (typeof data.country === "string" && /^[A-Z]{2}$/.test(data.country)) {
      geoCountry.set(data.country)
    }
  } catch {
    // Network/edge hiccup — stay on the safe gross default.
  }
}

// The monthly anchor a card should display for the resolved country. A plan
// with no pound price shows dollars to a UK visitor, which is what
// PricingRegion.charge_currency then charges.
export function pickAnchor(plan: PlanCard, key: AnchorKey): PlanAnchor {
  return plan.prices_month[key] ?? plan.prices_month.usd
}

// The yearly anchor, or null on a monthly-only tier — the caller renders the
// yearly line and its subscribe button only when this is non-null.
export function pickYearAnchor(
  plan: PlanCard,
  key: AnchorKey,
): PlanAnchor | null {
  return plan.prices_year
    ? (plan.prices_year[key] ?? plan.prices_year.usd)
    : null
}
